import { DeleteOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Progress } from 'components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import dayjs, { Dayjs } from 'dayjs';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import posthog from 'posthog-js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Day cell styles. Later entries win over earlier ones (merged with `cn`).
const CELL_CLASS =
  'relative flex aspect-square cursor-pointer flex-col items-center justify-center border-2 border-background bg-background text-base text-muted-foreground';
const CELL_IN_MONTH_CLASS = 'text-foreground';
const CELL_TODAY_CLASS = 'font-bold';
const CELL_FUTURE_CLASS = 'cursor-not-allowed text-subtle-foreground';
const CELL_SELECTED_CLASS = 'bg-destructive-muted border-destructive-border';
const CELL_SELECTED_EDGE_CLASS = 'border-chart-severity-very-bad';
const RANGE_BASE_CLASS = 'bg-destructive-muted border-chart-severity-very-bad';
const RANGE_POSITION_CLASS = {
  single: 'rounded-lg',
  start: 'rounded-l-lg',
  middle: '',
  end: 'rounded-r-lg',
} as const;
const PENDING_START_CLASS =
  'border-transparent outline-2 outline-dashed -outline-offset-4 outline-chart-severity-very-bad';

export type PeriodQuickFillModalProps = {
  open: boolean;
  onClose: () => void;
  onValidate: () => void;
};

export default function PeriodQuickFillModal({ open, onClose, onValidate }: PeriodQuickFillModalProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitScroll = useRef(false);
  const isLoadingMorePast = useRef(false);

  const { fillPeriodDays, removePeriodRange, getSpecificDailyStatus, cycleHistory } = useSymptomTrackingData();

  const [anchorMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [months, setMonths] = useState<Dayjs[]>([]);
  const [selection, setSelection] = useState<{ start?: Dayjs; end?: Dayjs }>({});
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  const numberOfCycles = cycleHistory.length;
  const progress = Math.min(100, Math.round((numberOfCycles / 3) * 100));

  const weekdayLabels = useMemo(() => {
    const weekStart = dayjs().startOf('week');
    return Array.from({ length: 7 }).map((_, i) => weekStart.add(i, 'day').format('dd').slice(0, 2));
  }, []);

  const buildMonthDays = useCallback((monthStart: Dayjs): (Dayjs | null)[] => {
    const start = monthStart.startOf('month');
    const end = monthStart.endOf('month');

    // Leading/trailing blank cells align the 1st (and last) of the month to
    // its actual weekday column, without borrowing days from neighboring
    // months (which would show them under the wrong month header).
    const leadingBlanks = start.diff(start.startOf('week'), 'day');
    const trailingBlanks = end.endOf('week').diff(end, 'day');

    const days: (Dayjs | null)[] = new Array(leadingBlanks).fill(null);
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      days.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    days.push(...new Array(trailingBlanks).fill(null));

    return days;
  }, []);

  // Helpers to detect period ranges in existing data
  const isPeriodDay = useCallback(
    (d: Dayjs) => {
      const status = getSpecificDailyStatus(d);
      return Boolean(status && status.menstruation_amount > 0);
    },
    [getSpecificDailyStatus],
  );

  const getRangePosition = useCallback(
    (d: Dayjs): 'single' | 'start' | 'middle' | 'end' | undefined => {
      if (!isPeriodDay(d)) return undefined;
      const prev = d.subtract(1, 'day');
      const next = d.add(1, 'day');
      const prevIs = isPeriodDay(prev);
      const nextIs = isPeriodDay(next);
      if (!prevIs && !nextIs) return 'single';
      if (!prevIs && nextIs) return 'start';
      if (prevIs && nextIs) return 'middle';
      if (prevIs && !nextIs) return 'end';
      return undefined;
    },
    [isPeriodDay],
  );

  // Find and delete entire period range starting from any day in the range
  const deletePeriodRange = useCallback(
    (clickedDay: Dayjs) => {
      if (!isPeriodDay(clickedDay)) return;

      // Find the start of the range by going backwards
      let rangeStart = clickedDay;
      while (rangeStart.subtract(1, 'day').isValid() && isPeriodDay(rangeStart.subtract(1, 'day'))) {
        rangeStart = rangeStart.subtract(1, 'day');
      }

      // Find the end of the range by going forwards
      let rangeEnd = clickedDay;
      while (rangeEnd.add(1, 'day').isValid() && isPeriodDay(rangeEnd.add(1, 'day'))) {
        rangeEnd = rangeEnd.add(1, 'day');
      }

      // Remove all days in the range with a single request
      removePeriodRange(rangeStart, rangeEnd);

      // Hide tooltip after deletion and clear any pending selection
      setTooltipVisible(null);
      setSelection({});
    },
    [isPeriodDay, removePeriodRange],
  );

  // Initialize with several months, ordered oldest -> newest so current month is at the bottom
  useEffect(() => {
    const initial: Dayjs[] = [];
    for (let i = 0; i < 12; i += 1) {
      initial.push(anchorMonth.subtract(i, 'month'));
    }
    setMonths(initial.reverse());
  }, [anchorMonth]);

  // Keep latest month visible at bottom only once when opening
  useEffect(() => {
    if (open && !didInitScroll.current) {
      didInitScroll.current = true;
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
    if (!open) {
      didInitScroll.current = false;
    }
    // Always reset tooltip visibility and selection when modal open state changes
    setTooltipVisible(null);
    setSelection({});
  }, [open]);

  // Older months are prepended, which would push the visible weeks down by
  // their height: the scroll offset is corrected by that height once they
  // have rendered (see the layout effect below).
  const prependedHeightRef = useRef<number | null>(null);

  const loadMorePast = useCallback(() => {
    if (isLoadingMorePast.current) return;
    isLoadingMorePast.current = true;
    prependedHeightRef.current = scrollRef.current?.scrollHeight ?? null;

    setMonths(prev => {
      const earliest = prev[0];
      const nexts: Dayjs[] = [];
      for (let i = 6; i >= 1; i -= 1) {
        nexts.push(earliest.subtract(i, 'month'));
      }
      return [...nexts, ...prev];
    });
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const before = prependedHeightRef.current;
    if (!el || before === null) return;
    prependedHeightRef.current = null;
    el.scrollTop += el.scrollHeight - before;
    // the next batch can load once this one is in place
    isLoadingMorePast.current = false;
  }, [months]);

  // Older months load as the top comes near, however the list got there
  // (wheel, touch, scrollbar); a batch is in flight until it has rendered.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el && el.scrollTop < 120) loadMorePast();
  }, [loadMorePast]);

  const isSelected = useCallback(
    (d: Dayjs) => {
      if (!selection.start || !selection.end) return false;
      return d.isSameOrAfter(selection.start, 'day') && d.isSameOrBefore(selection.end, 'day');
    },
    [selection],
  );

  const onDayClick = useCallback(
    (d: Dayjs) => {
      const today = dayjs();
      if (d.isAfter(today, 'day')) return;

      const status = getSpecificDailyStatus(d);
      const isPeriod = status && status.menstruation_amount > 0;

      // Hide any visible tooltips when clicking
      setTooltipVisible(null);

      // If we have a pending selection (start but no end), treat any click as the end point
      if (selection.start && !selection.end) {
        let start = selection.start;
        let end = d;
        if (d.isBefore(start, 'day')) {
          start = d;
          end = selection.start;
        }
        setSelection({ start, end });
        // Apply selection as period days
        const length = end.diff(start, 'day') + 1;
        fillPeriodDays(start, length);
        posthog.capture('tracking.period_quickfilled_one_cycle');
        return;
      }

      // If clicking on an existing period day and no pending selection, show tooltip for deletion
      if (isPeriod) {
        const dayKey = d.format('YYYY-MM-DD');
        setTooltipVisible(tooltipVisible === dayKey ? null : dayKey);
        return;
      }

      // Selection mode: first click sets start
      if (!selection.start || (selection.start && selection.end)) {
        setSelection({ start: d, end: undefined });
        posthog.capture('tracking.period_quickfill_started');
        return;
      }
    },
    [selection, getSpecificDailyStatus, fillPeriodDays, tooltipVisible],
  );

  const handleClose = useCallback(() => {
    setTooltipVisible(null);
    setSelection({});
    onClose();
  }, [onClose]);

  // Oldest first, so the current month sits at the bottom, where the list
  // opens (a column-reverse layout would do the same, but the dialog's scroll
  // lock reads such a container as already at its top and swallows the scroll).
  const monthsForRender = months;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('symptomTracking.cycles.fillPastCycles', 'Renseigner mes cycles')}</DialogTitle>
        </DialogHeader>
        <div>
          <div className="relative max-h-[50vh]">
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-12 bg-linear-to-b from-card to-card/0" />
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex max-h-[50vh] flex-col overflow-y-auto pr-2 [scrollbar-width:none]"
            >
              {monthsForRender.map((m, idx) => {
                const days = buildMonthDays(m);
                const monthKey = m.format('YYYY-MM');
                return (
                  <div key={`${monthKey}-${idx}`} className="mb-2" data-month={monthKey}>
                    <div className="top-0 py-2 font-semibold text-foreground capitalize">{m.format('MMMM YYYY')}</div>
                    <div className="mb-1 grid grid-cols-7 gap-1">
                      {weekdayLabels.map((label, i) => (
                        <div
                          key={`weekday-${monthKey}-${i}`}
                          className="text-center text-sm text-subtle-foreground capitalize"
                        >
                          {label}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((d, i) => {
                        if (!d) {
                          return <div key={`blank-${monthKey}-${i}`} className="aspect-square bg-background/30" />;
                        }

                        const today = dayjs();
                        const isToday = d.isSame(today, 'day');
                        const isFuture = d.isAfter(today, 'day');

                        const sel = isSelected(d);
                        const isEdge = sel && (d.isSame(selection.start, 'day') || d.isSame(selection.end, 'day'));
                        const pos = getRangePosition(d);
                        const isPendingStart = !selection.end && selection.start && d.isSame(selection.start, 'day');
                        const dayKey = d.format('YYYY-MM-DD');

                        const className = cn(
                          CELL_CLASS,
                          // Determine base class based on day type
                          isFuture
                            ? CELL_FUTURE_CLASS
                            : isToday
                              ? [CELL_IN_MONTH_CLASS, CELL_TODAY_CLASS]
                              : CELL_IN_MONTH_CLASS,
                          sel && [CELL_IN_MONTH_CLASS, CELL_SELECTED_CLASS],
                          isEdge && CELL_SELECTED_EDGE_CLASS,
                          // visualize existing ranges
                          pos && [CELL_IN_MONTH_CLASS, RANGE_BASE_CLASS, RANGE_POSITION_CLASS[pos]],
                          // pending start
                          isPendingStart && [RANGE_BASE_CLASS, RANGE_POSITION_CLASS.start, PENDING_START_CLASS],
                        );

                        const cell = (
                          <div
                            key={`${dayKey}-${i}`}
                            data-testid={`quickfill-${dayKey}`}
                            data-selected={sel}
                            className={className}
                            onClick={() => onDayClick(d)}
                          >
                            {d.date()}
                          </div>
                        );

                        if (!isPeriodDay(d)) return cell;

                        return (
                          <Tooltip key={`tooltip-${dayKey}-${i}`} open={tooltipVisible === dayKey}>
                            <TooltipTrigger asChild>{cell}</TooltipTrigger>
                            <TooltipContent side="top">
                              <div
                                className="mt-1 cursor-pointer"
                                onClick={e => {
                                  e.stopPropagation();
                                  deletePeriodRange(d);
                                }}
                              >
                                <DeleteOutlined className="mr-2" />
                                {t('symptomTracking.cycles.deleteCycle')}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="my-3 text-center">
            <InfoCircleFilled /> {t('symptomTracking.cycles.fillAtLeastThreeCycles')}
          </div>
          <div className="mb-3 flex items-center gap-3">
            <Progress value={progress} className="flex-1" />
            <span className="text-sm text-muted-foreground">{`${numberOfCycles}/3`}</span>
          </div>
        </div>
        <DialogFooter className="flex-row justify-end">
          <Button size="lg" variant="ghost" onClick={handleClose}>
            {t('common.later', 'Plus tard')}
          </Button>
          <Button size="lg" onClick={onValidate} disabled={numberOfCycles < 3}>
            {t('symptomTracking.cycles.validate', 'Validate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
